import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Eye, Pencil, Plus, RefreshCw, Search, Trash2, X, GripVertical } from 'lucide-react';
import Modal from '../../../components/Modal';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';

const trunc = (s, n = 25) => s && s.length > n ? s.slice(0, n) + '…' : (s || '');

const EMPTY_FORM = {
  service_name: '', service_code: '', description: '',
  introduction_date: '', normal_processing_days: 1,
  major_activities: [], deliverables: [], is_active: true,
  visible_to_user_ids: [],
};

function StatusPill({ active }) {
  return (
    <span className={`status-pill ${active ? 'status-done' : 'status-cancelled'}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: active ? 'var(--status-success-fg)' : 'var(--text-muted)', flexShrink: 0 }} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function ListEditor({ label, items, onChange, maxItemLength = 200 }) {
  const [draft, setDraft] = useState('');

  function add() {
    const val = draft.trim();
    if (!val) return;
    onChange([...items, val]);
    setDraft('');
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <input
          className="form-input"
          value={draft}
          maxLength={maxItemLength}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={`Add ${label.toLowerCase()} item…`}
          style={{ flex: 1 }}
        />
        <button className="btn btn-secondary btn-sm" type="button" onClick={add} style={{ whiteSpace: 'nowrap' }}>+ Add</button>
      </div>
      <div>
        {items.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--bg-muted)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '5px 10px', marginBottom: 4,
          }}>
            <GripVertical size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 13, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{item}</span>
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex' }}>
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MemberPicker({ members, value, onChange }) {
  const [search, setSearch] = React.useState('');
  function toggle(userId) {
    const id = Number(userId);
    onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  }
  const filtered = search.trim()
    ? members.filter(m => m.name?.toLowerCase().includes(search.toLowerCase()))
    : members;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-muted)' }}>
      <div style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)' }}>
        <input type="text" placeholder="Search members…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)', outline: 'none' }} />
      </div>
      <div style={{ maxHeight: 160, overflowY: 'auto' }}>
        {filtered.length === 0 && <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No members found</div>}
        {filtered.map(m => {
          const checked = value.includes(Number(m.user_id));
          return (
            <label key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', background: checked ? 'var(--bg-card)' : 'transparent' }}>
              <input type="checkbox" checked={checked} onChange={() => toggle(m.user_id)}
                style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>{m.name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function Services() {
  const { appId } = useParams();
  const { myRole, me } = useWorkspace();
  const api = useApi();

  const isAdmin = true; // all members can add services
  const isRoleAdmin = myRole === 'super_admin' || myRole === 'admin';
  const myUserId = me?.id;
  // Editing/deleting (unlike creating) is admin- or creator-only server-side
  // (see PATCH/DELETE /:id in backend/contracts/services.js) — everyone else
  // would get a 403, so those icons are only shown to whoever can use them.
  const canEditService = s => isRoleAdmin || String(s.created_by_user_id) === String(myUserId);

  const [services, setServices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [activeFilter, setActive] = useState('');
  const [rev, setRev]             = useState(0);
  const [members, setMembers]     = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [saveErr, setSaveErr]     = useState(null);

  useEffect(() => {
    api.listAppMembers(appId).then(data => {
      setMembers(((data && data.items) || []).map(m => ({
        user_id: m.user_id || m.id,
        name: m.name || [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email,
        role: m.role,
      })));
    }).catch(() => {});
  }, [appId]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (activeFilter !== '') params.active = activeFilter;
      setServices(await api.listContractServices(appId, params));
    } catch (e) {
      setError(e.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [appId, search, activeFilter, rev]);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM, major_activities: [], deliverables: [], visible_to_user_ids: [] });
    setSaveErr(null);
    setModalOpen(true);
  }

  function openEdit(s) {
    setEditing(s);
    setForm({
      service_name:           s.service_name || '',
      service_code:           s.service_code || '',
      description:            s.description || '',
      introduction_date:      s.introduction_date ? s.introduction_date.split('T')[0] : '',
      normal_processing_days: s.normal_processing_days ?? 1,
      major_activities:       [...(s.major_activities ?? [])],
      deliverables:           [...(s.deliverables ?? [])],
      is_active:              s.is_active !== false,
      visible_to_user_ids:    [...(s.visible_to_user_ids ?? [])],
    });
    setSaveErr(null);
    setModalOpen(true);
  }

  async function save() {
    if (!form.service_name?.trim()) { setSaveErr('Service name is required'); return; }
    if (!form.service_code?.trim()) { setSaveErr('Service code is required'); return; }
    setSaving(true); setSaveErr(null);
    try {
      const body = {
        ...form,
        normal_processing_days: Number(form.normal_processing_days) || 1,
        introduction_date: form.introduction_date || null,
      };
      let updated;
      if (editing) {
        updated = await api.updateContractService(appId, editing.id, body);
        setServices(prev => prev.map(x => x.id === updated.id ? updated : x));
      } else {
        updated = await api.createContractService(appId, body);
        setServices(prev => [updated, ...prev]);
      }
      setModalOpen(false);
    } catch (e) {
      setSaveErr(e?.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(s) {
    if (!window.confirm(`Delete service "${s.service_name}"? This cannot be undone.`)) return;
    try {
      await api.deleteContractService(appId, s.id);
      setServices(prev => prev.filter(x => x.id !== s.id));
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Failed to delete');
    }
  }

  const activeCount   = services.filter(s => s.is_active).length;
  const inactiveCount = services.filter(s => !s.is_active).length;

  return (
    <div className="tl-page">
      {/* KPI strip */}
      {!loading && services.length > 0 && (
        <div className="home-stats-strip" style={{ gridTemplateColumns: 'repeat(3, minmax(100px, 180px))', marginBottom: 20 }}>
          <div className="stat-tile">
            <div className="stat-value">{services.length}</div>
            <div className="stat-label">Total Services</div>
          </div>
          <div className="stat-tile">
            <div className="stat-value" style={{ color: 'var(--status-success-fg)' }}>{activeCount}</div>
            <div className="stat-label">Active</div>
          </div>
          <div className="stat-tile">
            <div className="stat-value" style={{ color: 'var(--text-muted)' }}>{inactiveCount}</div>
            <div className="stat-label">Inactive</div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="tl-toolbar">
        <div className="tl-search-wrap">
          <Search className="tl-search-icon" size={14} />
          <input
            className="tl-search-input"
            placeholder="Search services…"
            value={search}
            maxLength={1000}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="tl-search-clear" onClick={() => setSearch('')}>×</button>}
        </div>
        <select className="tl-select" value={activeFilter} onChange={e => setActive(e.target.value)}>
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <button onClick={() => setRev(r => r + 1)} className="btn-secondary"
          style={{ padding: '8px 10px', display: 'flex', alignItems: 'center' }} title="Refresh">
          <RefreshCw size={14} />
        </button>
        {isAdmin && (
          <button className="btn-primary" onClick={openCreate}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Add Service
          </button>
        )}
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <div className="tl-loading"><span className="tl-spinner" /> Loading…</div>
      ) : services.length === 0 ? (
        <div className="tl-empty">
          <div className="tl-empty-title">No services found</div>
          <p className="tl-empty-sub">Add contract services to get started.</p>
        </div>
      ) : (
        <div className="entity-table-wrap">
          <table className="entity-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>Sl No</th>
                <th style={{ width: 300 }}>Service</th>
                <th style={{ width: 90 }}>Code</th>
                <th style={{ width: 130 }}>Processing Days</th>
                <th style={{ width: 140 }}>Created By</th>
                <th style={{ width: 120 }}>Visible To</th>
                <th style={{ width: 90 }}>Status</th>
                <th style={{ width: 100 }} className="row-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s, idx) => (
                <tr key={s.id}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'normal', wordBreak: 'break-word' }}>{s.service_name}</div>
                  </td>
                  <td>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--brand)', background: 'var(--bg-muted)', padding: '2px 7px', borderRadius: 4, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {s.service_code}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center' }}>
                    {s.normal_processing_days} day{s.normal_processing_days !== 1 ? 's' : ''}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'normal', wordBreak: 'break-word' }}>{s.created_by_name || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}
                    title={(s.visible_to_user_ids ?? []).length === 0 ? 'All' : (s.visible_to_user_ids).map(uid => { const m = members.find(x => Number(x.user_id) === Number(uid)); return m ? m.name : uid; }).join(', ')}>
                    {(s.visible_to_user_ids ?? []).length === 0 ? (
                      <span style={{ color: 'var(--text-muted)' }}>All</span>
                    ) : (
                      <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{(s.visible_to_user_ids).map(uid => {
                        const m = members.find(x => Number(x.user_id) === Number(uid));
                        return m ? m.name : uid;
                      }).join(', ')}</span>
                    )}
                  </td>
                  <td><StatusPill active={s.is_active} /></td>
                  <td className="row-actions">
                    <button className="btn-link-sm" onClick={() => setViewModal(s)} title="View details"><Eye size={13} /></button>
                    {canEditService(s) && (
                      <>
                        <button className="btn-link-sm" onClick={() => openEdit(s)} title="Edit"><Pencil size={13} /></button>
                        <button className="btn-link-sm" onClick={() => handleDelete(s)} title="Delete" style={{ color: 'var(--neg)' }}><Trash2 size={13} /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        title={editing ? `Edit Service — ${editing.service_name}` : 'Add Service'}
        onClose={() => setModalOpen(false)}
        size="lg"
        footer={
          <div className="form-actions">
            <button className="btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Update Service' : 'Create Service'}
            </button>
          </div>
        }
      >
        <div className="org-edit-form">
          <div className="org-form-section">
            <div className="org-form-section-title">Basic Info</div>
            <div className="org-form-grid">
              <label className="form-label">
                <span>Service Name <span style={{ color: 'var(--neg)' }}>*</span></span>
                <input className="form-input" value={form.service_name} maxLength={100}
                  onChange={e => setForm(p => ({ ...p, service_name: e.target.value }))}
                  placeholder="e.g. GST Filing" />
              </label>
              <label className="form-label">
                <span>Service Code <span style={{ color: 'var(--neg)' }}>*</span></span>
                <input className="form-input" value={form.service_code} maxLength={20}
                  onChange={e => setForm(p => ({ ...p, service_code: e.target.value }))}
                  placeholder="e.g. GST-FILING" />
              </label>
              <label className="form-label">
                <span>Normal Processing Days</span>
                <input className="form-input" type="number" min={1} value={form.normal_processing_days}
                  onKeyDown={e => { if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault(); }}
                  onChange={e => { const v = e.target.value; if (v === '' || (Number(v) >= 1 && String(Math.floor(Number(v))).length <= 4)) setForm(p => ({ ...p, normal_processing_days: v })); }} />
              </label>
              <label className="form-label">
                <span>Introduction Date</span>
                <input className="form-input" type="date" value={form.introduction_date}
                  onChange={e => setForm(p => ({ ...p, introduction_date: e.target.value }))} />
              </label>
              <label className="form-label" style={{ gridColumn: '1 / -1' }}>
                <span>Description</span>
                <textarea className="form-input" rows={2} value={form.description} maxLength={1000}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  style={{ resize: 'vertical' }} placeholder="Brief description…" />
              </label>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" id="svc-active" checked={form.is_active}
                  onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
                <label htmlFor="svc-active" style={{ fontSize: 13, margin: 0, cursor: 'pointer' }}>Active</label>
              </div>
            </div>
          </div>

          <div className="org-form-section">
            <div className="org-form-section-title">Visibility</div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Visible to (leave empty to show to all members):
              </div>
              <MemberPicker
                members={members}
                value={form.visible_to_user_ids}
                onChange={ids => setForm(p => ({ ...p, visible_to_user_ids: ids }))}
              />
              {form.visible_to_user_ids.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  {form.visible_to_user_ids.length} member{form.visible_to_user_ids.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          </div>

          <div className="org-form-section">
            <div className="org-form-section-title">Checklist</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
                  Major Activities
                </div>
                <ListEditor
                  label="Major Activities"
                  items={form.major_activities}
                  onChange={v => setForm(p => ({ ...p, major_activities: v }))}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
                  Deliverables
                </div>
                <ListEditor
                  label="Deliverables"
                  items={form.deliverables}
                  onChange={v => setForm(p => ({ ...p, deliverables: v }))}
                />
              </div>
            </div>
          </div>
        </div>
        {saveErr && <div className="banner banner-error" style={{ marginTop: 8 }}>{saveErr}</div>}
      </Modal>

      {/* View Modal */}
      <Modal open={!!viewModal} title="Service Details" onClose={() => setViewModal(null)} size="md"
        footer={null}
      >
        {viewModal && (
          <div className="org-edit-form">
            <div className="org-form-section">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  ['Service Name',      viewModal.service_name],
                  ['Service Code',      viewModal.service_code],
                  ['Processing Days',   `${viewModal.normal_processing_days} day${viewModal.normal_processing_days !== 1 ? 's' : ''}`],
                  ['Introduction Date', viewModal.introduction_date ? String(viewModal.introduction_date).slice(0, 10) : '—'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 3 }}>{k}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{v}</div>
                  </div>
                ))}
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 3 }}>Status</div>
                  <StatusPill active={viewModal.is_active} />
                </div>
                {viewModal.description && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 3 }}>Description</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{viewModal.description}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="org-form-section">
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>Visible To</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {(viewModal.visible_to_user_ids ?? []).length === 0 ? (
                  <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>All members</span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(viewModal.visible_to_user_ids).map(uid => {
                      const m = members.find(x => Number(x.user_id) === Number(uid));
                      return (
                        <span key={uid} style={{ padding: '2px 10px', background: 'var(--bg-muted)', border: '1px solid var(--border)', borderRadius: 20, fontSize: 12, color: 'var(--text-primary)' }}>
                          {m ? m.name : uid}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="org-form-section">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
                    Major Activities ({(viewModal.major_activities ?? []).length})
                  </div>
                  {(viewModal.major_activities ?? []).length === 0 ? (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>None defined</p>
                  ) : (viewModal.major_activities ?? []).map((a, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#ede9fe', color: '#5b21b6', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                      <span style={{ fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>{a}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
                    Deliverables ({(viewModal.deliverables ?? []).length})
                  </div>
                  {(viewModal.deliverables ?? []).length === 0 ? (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>None defined</p>
                  ) : (viewModal.deliverables ?? []).map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#f0fdf4', color: '#166534', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span style={{ fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>{d}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
